import React, { useEffect, useMemo, useState } from 'react'
import {
  SmartList,
  IColumnElement,
  IHeaderItem,
  Icons,
  IconButton,
  SearchInput,
  PaginationPanel,
  Spinner,
  IRowElement,
  Container,
  Row,
} from '@doseme/cohesive-ui'
import { NAVY} from '@doseme/cohesive-ui'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IEntityHashMap, isLoading } from 'flux-entities'

import { TProps } from './types'
import { usersEdit, USERS_CREATE_URL } from '../../../../constants/urls/frontend'
import { IUser } from '../../../../types/user'
import { DISPLAY_ITEMS_PER_PAGE } from '../../../../constants/display'
import { paginate } from '../../../utils'
import { smartListData } from '../../utils'
import { IInstallation } from '../../../../types/installation'
import { IUsersState } from '../../../../store/users/types'
import { alphabetical } from '../../../../utils/arrayUtils'

const cols: IHeaderItem[] = [
  {
    name: 'Username'
  },
  {
    name: 'Installation'
  },
  {
    name: 'Last Login'
  },
  {
    name: 'Actions',
  }
]

const textContent = (user: IUser, allInstallations: IEntityHashMap<IInstallation>, ): string[] => [
  user.email,
  allInstallations[user.installation] ? allInstallations[user.installation].code : ''
]

const getSearchableContent = (
  userIds: Array<number | string>,
  allUsers: IEntityHashMap<IUser>,
  allInstallations: IEntityHashMap<IInstallation>
) => userIds.reduce<Record<number, string[]>>(
  (acc, x) => {
    return {
      ...acc,
      [x]: textContent(allUsers[x], allInstallations)
    }
  }, {}
)

const getPaginatedListWithPageCount = (
  isPageLoading: boolean,
  users: IUsersState,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnContent: (user: IUser) => IColumnElement[],
  currentPage: number,
  updateCurrentPage: (pages: number) => void,
): { totalPages: number, paginatedData: IRowElement[] } => {
  let data: IRowElement[] = []

  if (!isPageLoading) {
    data = smartListData({
      ids: users.ids.sort(
        (x, y) => alphabetical(
          users.all[x].email,
          users.all[y].email
        )
      ),
      all: users.all,
      searchContent,
      searchText,
      columnizeRow: columnContent,
    })
  }

  const totalPages = Math.ceil(data.length / DISPLAY_ITEMS_PER_PAGE) || 1

  if (currentPage > totalPages) {
    updateCurrentPage(totalPages)
  }

  return {
    totalPages,
    paginatedData: paginate(data, { currentPage, itemsPerPage: DISPLAY_ITEMS_PER_PAGE })
  }
}

const Display: React.FC<TProps> = (props: TProps): JSX.Element | null => {
  const {
    users,
    fetchUsers,
    installations,
    fetchInstallations,
    history,
  } = props

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [installations.touched, fetchInstallations])

  useEffect(() => {
    if (!users.touched) { fetchUsers() }
  }, [users.touched, fetchUsers])

  const [currentPage, updateCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')

  const searchableContent = useMemo(() => getSearchableContent(
    users.ids,
    users.all,
    installations.all
  ), [
    users.ids,
    users.all,
    installations.all
  ])

  if (!users.touched || !installations.touched) {
    return null
  }

  const columnContent = (user: IUser): IColumnElement[] => {
    const lastLogin = user.lastLogin.isValid() ? user.lastLogin.toString() : '-'
    return [
      {
        name: 'username',
        element: (
          <span>
            {user.email}
          </span>
        ),
        text: user.email,
      },

      {
        name: 'installation',
        element: (
          <span>
            {installations.all[user.installation].code}
          </span>
        ),
        text: installations.all[user.installation].code,
      },

      {
        name: 'lastLogin',
        element: (
          <span>
            {lastLogin}
          </span>
        ),
        text: lastLogin,
      },

      {
        name: 'actions',
        element: (
          <div className='p-3'>
            <Icons.EditIconButton
              data-test='edit-button'
              onClick={() => history.push(usersEdit(user.id))}
            />
          </div>
        )
      }
    ]
  }

  const isPageLoading = isLoading(users) || isLoading(installations)

  const { totalPages, paginatedData } = getPaginatedListWithPageCount(
    isPageLoading,
    users,
    searchableContent,
    searchText,
    columnContent,
    currentPage,
    updateCurrentPage
  )

  return (
    <Container>
      <div className='d-flex justify-content-between my-2'>
        <div className='d-flex align-items-center'>
          <h4 className='co-h4 pr-2' data-test='users-list'>Users List</h4>
          {
            !isPageLoading
              ? <IconButton
                onClick={() => history.push(USERS_CREATE_URL)}
                size='30px'
              >
                <FontAwesomeIcon icon={faPlus} />
              </IconButton>
              : <Spinner color={NAVY} size='lg' />
          }
        </div>
        <SearchInput
          value={searchText}
          onChange={setSearchText}
        />
      </div>
      <Row>
        <SmartList
          loading={isLoading(users) || isLoading(installations)}
          cols={cols}
          data={paginatedData}
          textIfEmpty={users.ids.length ? 'No matching users found.' : 'There are currently no users.'}
        />
      </Row>
      <Row className='mt-2'>
        <PaginationPanel
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={updateCurrentPage}
        />
      </Row>
    </Container>
  )
}

export {
  Display
}
